/**
 * 役所資料取得代行モーダルで使用する styled コンポーネント。
 * RealEstateDocumentFetchModal.tsx から抽出。
 */

import {
  Box,
  Dialog,
  DialogContent,
  FormControlLabel,
  Checkbox,
  Tooltip,
  TextField,
  IconButton,
  Paper,
  styled,
} from "@mui/material";

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "8px",
    minWidth: "320px",
    maxWidth: "1000px",
    width: "90vw",
    margin: "16px",
    [theme.breakpoints.up("sm")]: {
      width: "85vw",
      margin: "32px",
    },
    [theme.breakpoints.up("md")]: {
      width: "900px",
    },
    [theme.breakpoints.up("lg")]: {
      width: "1000px",
    },
  },
  // fullScreen を指定した時（SP）は、上の幅・余白・角丸を外して画面いっぱいにする
  "& .MuiDialog-paperFullScreen": {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    margin: 0,
    borderRadius: 0,
  },
}));

export const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: "16px",
  paddingTop: "12px",
  [theme.breakpoints.up("sm")]: {
    padding: "20px",
    paddingTop: "16px",
  },
  [theme.breakpoints.up("md")]: {
    padding: "24px",
    paddingTop: "16px",
  },
}));

export const HeaderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "20px",
  padding: "8px 0",
  flexWrap: "wrap",
  gap: "8px",
  [theme.breakpoints.up("sm")]: {
    marginBottom: "24px",
    padding: "12px 0",
  },
  [theme.breakpoints.up("md")]: {
    marginBottom: "28px",
    padding: "16px 0",
  },
}));

export const SectionContainer = styled(Paper)(({ theme }) => ({
  padding: "0px",
  marginBottom: "24px",
  borderRadius: "16px",
  border: "none",
  background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  overflow: "hidden",
}));

export const SectionHeader = styled(Box)(({ theme }) => ({
  padding: "20px 24px",
  cursor: "pointer",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderRadius: "16px 16px 0 0",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(59, 130, 246, 0.05)",
  },
}));

export const SectionContent = styled(Box)(({ theme }) => ({
  padding: "0 24px 24px 24px",
}));

export const CheckboxGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "12px",
  [theme.breakpoints.up("sm")]: {
    gridTemplateColumns: "1fr",
    gap: "16px",
  },
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "18px",
  },
  [theme.breakpoints.up("lg")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
  },
}));

export const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  margin: 0,
  padding: "16px 20px",
  borderRadius: "12px",
  border: "2px solid #e2e8f0",
  backgroundColor: "#ffffff",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  minHeight: "64px",
  minWidth: "200px",
  alignItems: "center",
  display: "flex",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    backgroundColor: "#f1f5f9",
    borderColor: "#cbd5e1",
    transform: "translateY(-1px)",
  },
  "& .MuiCheckbox-root": {
    padding: "0",
    marginRight: "12px",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  "& .MuiFormControlLabel-label": {
    fontSize: "inherit",
    lineHeight: 1.5,
    flex: 1,
    minWidth: 0,
    margin: 0,
  },
  "&.Mui-disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  // チェック済みの状態
  '&[data-checked="true"]': {
    borderColor: "#3b82f6",
    backgroundColor: "#eff6ff",
    "& .MuiFormControlLabel-label": {
      fontWeight: 600,
      color: "#1e40af",
    },
  },
}));

export const SubCategoryContainer = styled(Box)(({ theme }) => ({
  marginTop: "16px",
  marginLeft: "0px",
  padding: "20px",
  backgroundColor: "#f1f5f9",
  borderRadius: "12px",
  border: "2px solid #e2e8f0",
}));

export const SubCategoryGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "12px",
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "16px",
  },
}));

export const StyledSubFormControlLabel = styled(FormControlLabel)(
  ({ theme }) => ({
    margin: 0,
    padding: "12px 16px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    transition: "all 0.3s ease",
    cursor: "pointer",
    minHeight: "44px",
    minWidth: "120px",
    alignItems: "center",
    display: "flex",
    "&:hover": {
      backgroundColor: "#f8fafc",
      borderColor: "#cbd5e1",
      transform: "translateY(-1px)",
    },
    "& .MuiCheckbox-root": {
      padding: "0",
      marginRight: "10px",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    "& .MuiFormControlLabel-label": {
      fontSize: "0.875rem",
      lineHeight: 1.3,
      flex: 1,
      minWidth: 0,
      margin: 0,
      fontWeight: 500,
    },
    // チェック済みの状態
    '&[data-checked="true"]': {
      borderColor: "#3b82f6",
      backgroundColor: "#eff6ff",
      "& .MuiFormControlLabel-label": {
        fontWeight: 600,
        color: "#1e40af",
      },
    },
  })
);

export const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  color: "#94a3b8",
  padding: "0",
  "&.Mui-checked": {
    color: "#3b82f6",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "24px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "26px",
    },
  },
  "&:hover": {
    backgroundColor: "transparent",
    "& .MuiSvgIcon-root": {
      transform: "scale(1.1)",
    },
  },
  "&.Mui-checked:hover": {
    "& .MuiSvgIcon-root": {},
  },
}));

export const StyledTooltip = styled(Tooltip)(({ theme }) => ({
  "& .MuiTooltip-tooltip": {
    backgroundColor: "#1e293b",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "10px",
    padding: "10px 16px",
    maxWidth: "320px",
    lineHeight: 1.5,
    backdropFilter: "blur(10px)",
  },
  "& .MuiTooltip-arrow": {
    color: "#1e293b",
    "&::before": {
      backgroundColor: "#1e293b",
    },
  },
}));

export const CustomInputContainer = styled(Box)(({ theme }) => ({
  marginTop: "32px",
  padding: "24px",
  backgroundColor: "#f8fafc",
  borderRadius: "16px",
  border: "2px solid #e2e8f0",
}));

export const CustomInputItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "16px",
  "&:last-child": {
    marginBottom: 0,
  },
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "#e2e8f0",
    },
    "&:hover fieldset": {
      borderColor: "#cbd5e1",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3b82f6",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "0.875rem",
    padding: "12px 14px",
  },
}));

export const AddRemoveButton = styled(IconButton)(({ theme }) => ({
  width: "36px",
  height: "36px",
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  "&:hover": {
    backgroundColor: "#f1f5f9",
    borderColor: "#cbd5e1",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
  },
}));

export const CommentContainer = styled(Box)(({ theme }) => ({
  marginTop: "32px",
  padding: "24px",
  backgroundColor: "#f8fafc",
  borderRadius: "16px",
  border: "2px solid #e2e8f0",
}));

export const StyledTextArea = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    "& fieldset": {
      borderColor: "#e2e8f0",
    },
    "&:hover fieldset": {
      borderColor: "#cbd5e1",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3b82f6",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "0.875rem",
    lineHeight: "1.6",
    padding: "16px",
  },
}));

export const DisclaimerContainer = styled(Box)(({ theme }) => ({
  marginTop: "24px",
  padding: "20px",
  backgroundColor: "#fef3c7",
  borderRadius: "12px",
  border: "2px solid #f59e0b",
}));

export const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: "8px",
  marginTop: "20px",
  flexDirection: "column",
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    gap: "12px",
    marginTop: "24px",
  },
}));

// 最終確認モーダル用のスタイルドコンポーネント

export const ConfirmSection = styled(Box)(({ theme }) => ({
  marginBottom: "24px",
  padding: "20px",
  backgroundColor: "#f8fafc",
  borderRadius: "12px",
  border: "1px solid #e2e8f0",
}));

export const CostSection = styled(Box)(({ theme }) => ({
  marginBottom: "24px",
  padding: "20px",
  backgroundColor: "#ecfdf5",
  borderRadius: "12px",
  border: "2px solid #10b981",
}));

export const CostItem = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 12px",
  marginBottom: "8px",
  backgroundColor: "#ffffff",
  borderRadius: "8px",
  border: "1px solid #d1fae5",
  "&:last-child": {
    marginBottom: 0,
  },
}));

export const TotalCostItem = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 20px",
  marginTop: "16px",
  backgroundColor: "#065f46",
  borderRadius: "12px",
  color: "#ffffff",
  fontWeight: 700,
  fontSize: "1.1rem",
}));
